import { useEffect, useId, useMemo, useReducer, useRef, useState, type DragEvent } from 'react';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { ACCEPT_ATTR } from '../../contractor-payments/fileCheck';
import { formLabels } from '../types';
import { OUTSIDE_PAYMENT_METHODS, type DashboardDetail, type OutsidePaymentMethod } from '../dashboardTypes';
import { formatBytes, formatMoney, todayIso } from '../format';
import { admitFiles, checkEntry, filesAllowSave, filesBlockingSave, filesReducer, MAX_OUTSIDE_FILES, type FileEntry } from '../drawer/outsideFiles';
import { describeFailure, newIdempotencyKey, type Failure } from '../drawer/failures';
import { taxPaymentsGateway } from '../index';

const REFERENCE = /^[A-Za-z0-9._:/# -]{1,100}$/;
const LONG_NUMBER = /\d[\d\s-]{7,}\d/;
const MONEY = /^\d{1,13}(\.\d{1,2})?$/;

type Field = 'date' | 'amount' | 'method' | 'reference' | 'notes' | 'ack';

/**
 * Record Outside Payment (Figma S15, S27 to S29, S38). Records a tax payment that was ALREADY made outside Escochex, for history and reconciliation. It moves no
 * money and calls no payment partner. The files travel with the save in one request and the server stores every file before it saves the record, so a file only
 * reads "Success" after the record exists. Save stays disabled until the form is valid. One idempotency key per attempt: it is kept after a failure whose outcome is
 * unknown (so a retry cannot record twice) and replaced after a clear refusal (so corrections are not swallowed). The server remains the authority on every rule.
 */
export function RecordOutsidePaymentDialog({ detail, companyName, onClose, onRecorded }: {
  detail: DashboardDetail;
  companyName: string | null;
  onClose: () => void;
  /** Called with the server's updated payment once the record is saved. */
  onRecorded: (updated: DashboardDetail) => void;
}) {
  const uid = useId();
  const { row } = detail;
  const fileInput = useRef<HTMLInputElement>(null);
  const keyRef = useRef(newIdempotencyKey());
  const [date, setDate] = useState('');
  const [amount, setAmount] = useState(row.amount.toFixed(2));
  const [method, setMethod] = useState<'' | OutsidePaymentMethod>('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [ack, setAck] = useState(false);
  const [files, dispatch] = useReducer(filesReducer, [] as FileEntry[]);
  const [touched, setTouched] = useState<ReadonlySet<Field>>(new Set());
  const [refused, setRefused] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [dragging, setDragging] = useState(false);
  const submitting = useRef(false);

  const errors = useMemo(() => {
    const e: Partial<Record<Field, string>> = {};
    if (!date) e.date = 'Enter the payment date.';
    else if (date > todayIso()) e.date = 'The payment date can’t be in the future.';
    else if (date < '2000-01-01') e.date = 'Enter a date from the year 2000 on.';
    if (!MONEY.test(amount.trim()) || Number(amount) <= 0) e.amount = 'Enter an amount greater than zero, with at most two decimals.';
    else if (Number(amount) !== row.amount) e.amount = `The amount must equal the payment amount (${formatMoney(row.amount)}). A partial or different payment can’t be recorded here yet.`;
    if (!method) e.method = 'Choose a payment method.';
    if (reference.trim() && !REFERENCE.test(reference.trim())) e.reference = 'Use 1 to 100 letters, digits or . _ : / # - characters.';
    if (notes.trim().length > 500) e.notes = 'Notes must be 500 characters or fewer.';
    else if (LONG_NUMBER.test(notes)) e.notes = 'Don’t enter an account or other long number in the notes.';
    if (!ack) e.ack = 'Confirm that this payment was completed outside Escochex.';
    return e;
  }, [date, amount, method, reference, notes, ack, row.amount]);

  const filesOk = filesAllowSave(files);
  const valid = Object.keys(errors).length === 0 && filesOk;
  const show = (f: Field) => (touched.has(f) ? errors[f] : undefined);
  const touch = (f: Field) => setTouched((t) => (t.has(f) ? t : new Set(t).add(f)));
  const edit = () => { if (failure) setFailure(null); };

  useEffect(() => {
    // Each newly added file is checked once; the result is applied only to a file still being checked.
    for (const entry of files.filter((f) => f.state === 'checking')) {
      void checkEntry(entry).then((r) => dispatch({ type: 'checked', id: entry.id, ok: r.ok, message: r.message }));
    }
  }, [files]);

  function take(list: FileList | File[] | null | undefined) {
    if (!list) return;
    edit();
    const { entries, refused: notTaken } = admitFiles(files, Array.from(list));
    setRefused(notTaken);
    if (entries.length > 0) dispatch({ type: 'add', entries });
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    if (!busy) take(e.dataTransfer.files);
  }

  async function save() {
    setTouched(new Set<Field>(['date', 'amount', 'method', 'reference', 'notes', 'ack']));
    if (!valid || busy || submitting.current || method === '') return;
    submitting.current = true;
    setBusy(true);
    setFailure(null);
    dispatch({ type: 'upload-started' });
    try {
      const updated = await taxPaymentsGateway.recordOutsidePayment(row.id, {
        paymentDate: date, amount: Number(amount), method, referenceNumber: reference, notes, acknowledged: ack, files: files.map((f) => f.file),
      }, keyRef.current);
      dispatch({ type: 'upload-succeeded' });
      onRecorded(updated);
    } catch (err) {
      const result = describeFailure(err, 'The outside payment couldn’t be recorded. Nothing was saved. Try again.');
      // A clear refusal is final for this key: the next attempt is a new one. An unknown outcome keeps the key so a retry cannot record twice.
      if (!result.outcomeUnknown) keyRef.current = newIdempotencyKey();
      dispatch({ type: 'upload-failed', message: 'Not uploaded. Nothing was saved.' });
      setFailure(result);
      setBusy(false);
      submitting.current = false;
    }
  }

  const err = (f: Field) => (show(f) ? <p className="jr-error" id={`${uid}-${f}-err`} role="alert">{errors[f]}</p> : null);
  const invalid = (f: Field) => (show(f) ? true : undefined);
  const blocking = filesBlockingSave(files);

  return (
    <ModalDialog
      title="Record Outside Tax Payment"
      size="md"
      className="tpd-modal"
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="jr-btn jr-btn--primary" disabled={busy || !valid} onClick={() => void save()}>{busy ? 'Saving…' : 'Save Record Outside Payment'}</button>
        </>
      )}
    >
      <p className="tpd-muted" data-testid="record-subtitle">
        {companyName ? `${companyName} · ` : ''}{row.agency} — {row.jurisdiction} · {formLabels[row.formCode] ?? row.formCode}
      </p>
      <p className="tpd-muted">Record a tax payment you already made outside Escochex, for history and reconciliation. This does not send, debit or pay anything.</p>

      <div className="tpd-form-row">
        <div className="jr-field">
          <label htmlFor={`${uid}-date`}>Payment Date <span className="jr-req" aria-hidden="true">*</span></label>
          <input id={`${uid}-date`} data-autofocus type="date" value={date} max={todayIso()} aria-required="true" aria-invalid={invalid('date')} aria-describedby={show('date') ? `${uid}-date-err` : undefined}
            onBlur={() => touch('date')} onChange={(e) => { setDate(e.target.value); edit(); }} />
          {err('date')}
        </div>
        <div className="jr-field">
          <label htmlFor={`${uid}-amount`}>Amount <span className="jr-req" aria-hidden="true">*</span></label>
          <input id={`${uid}-amount`} inputMode="decimal" value={amount} aria-required="true" aria-invalid={invalid('amount')} aria-describedby={show('amount') ? `${uid}-amount-err` : undefined}
            onBlur={() => touch('amount')} onChange={(e) => { setAmount(e.target.value); edit(); }} />
          {err('amount')}
        </div>
      </div>

      <div className="jr-field">
        <label htmlFor={`${uid}-method`}>Payment Method <span className="jr-req" aria-hidden="true">*</span></label>
        <select id={`${uid}-method`} value={method} aria-required="true" aria-invalid={invalid('method')} aria-describedby={show('method') ? `${uid}-method-err` : undefined}
          onBlur={() => touch('method')} onChange={(e) => { setMethod(e.target.value as OutsidePaymentMethod | ''); edit(); }}>
          <option value="">Select a method</option>
          {OUTSIDE_PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        {err('method')}
      </div>

      <div className="jr-field">
        <label htmlFor={`${uid}-ref`}>Agency Confirmation / Reference Number (optional)</label>
        <input id={`${uid}-ref`} value={reference} maxLength={100} placeholder="E.g. CHK-44821" aria-invalid={invalid('reference')} aria-describedby={show('reference') ? `${uid}-reference-err` : undefined}
          onBlur={() => touch('reference')} onChange={(e) => { setReference(e.target.value); edit(); }} />
        {err('reference')}
      </div>

      <div className="jr-field">
        <span id={`${uid}-docs`} className="tpd-label">Supporting Documents (optional, up to {MAX_OUTSIDE_FILES})</span>
        <div
          className={`tpd-drop${dragging ? ' tpd-drop--active' : ''}`} role="group" aria-labelledby={`${uid}-docs`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={onDrop}
        >
          <button type="button" className="tpd-link" disabled={busy || files.length >= MAX_OUTSIDE_FILES} onClick={() => fileInput.current?.click()}>Click to upload</button>
          <span> or drag and drop</span>
          <span className="tpd-drop__hint">PDF, JPG, PNG · Max 10 MB each</span>
          <input ref={fileInput} type="file" multiple className="tpd-visually-hidden" tabIndex={-1} aria-label="Supporting document files" accept={ACCEPT_ATTR}
            onChange={(e) => { take(e.target.files); e.target.value = ''; }} />
        </div>
        {refused.length > 0 && <ul className="tpd-file-refused" role="alert">{refused.map((r) => <li key={r}>{r}</li>)}</ul>}
        {files.length > 0 && (
          <ul className="tpd-files" aria-label="Attached files" aria-live="polite">
            {files.map((f) => (
              <li key={f.id} className={`tpd-file tpd-file--${f.state}`} data-state={f.state}>
                <span className="tpd-file__text">
                  <span className="tpd-file__name">{f.file.name}</span>
                  <span className="tpd-file__meta">{f.file.type || 'unknown type'} · {formatBytes(f.file.size)}</span>
                  <span className="tpd-file__state">
                    {f.state === 'checking' && 'Checking…'}
                    {f.state === 'ready' && 'Ready · sent when you save'}
                    {f.state === 'uploading' && 'Uploading…'}
                    {f.state === 'success' && 'Success'}
                    {f.state === 'invalid' && `Not accepted: ${f.message ?? 'this file can’t be attached.'}`}
                    {f.state === 'failed' && `Failed: ${f.message ?? 'not uploaded.'}`}
                  </span>
                </span>
                {f.state === 'failed' && <button type="button" className="tpd-link" disabled={busy || !valid} onClick={() => void save()} aria-label={`Retry ${f.file.name}`}>Retry</button>}
                <button type="button" className="tpd-link" disabled={busy || f.state === 'uploading' || f.state === 'success'} onClick={() => { dispatch({ type: 'remove', id: f.id }); setRefused([]); edit(); }} aria-label={`Remove ${f.file.name}`}>Remove</button>
              </li>
            ))}
          </ul>
        )}
        {blocking.some((f) => f.state === 'invalid') && <p className="jr-error" role="alert">Remove the files that aren’t accepted before saving.</p>}
      </div>

      <div className="jr-field">
        <label htmlFor={`${uid}-notes`}>Notes (optional)</label>
        <textarea id={`${uid}-notes`} rows={3} maxLength={500} value={notes} placeholder="Enter your notes here…" aria-invalid={invalid('notes')} aria-describedby={show('notes') ? `${uid}-notes-err` : undefined}
          onBlur={() => touch('notes')} onChange={(e) => { setNotes(e.target.value); edit(); }} />
        {err('notes')}
      </div>

      <div className="jr-field tpd-ack">
        <label className="tpd-check">
          <input type="checkbox" checked={ack} aria-required="true" aria-invalid={invalid('ack')} onChange={(e) => { setAck(e.target.checked); setTouched((t) => new Set(t).add('ack')); edit(); }} />
          <span>I confirm that this tax payment was completed outside Escochex. This record is for payment history and liability reconciliation only.</span>
        </label>
        {err('ack')}
      </div>

      {failure && (
        <div className="tpd-form-error" role="alert" data-testid="record-error">
          <p>{failure.message}</p>
          {failure.outcomeUnknown && <p className="tpd-muted">If you try again it will not be recorded twice.</p>}
        </div>
      )}
    </ModalDialog>
  );
}
